import { FieldType } from '@databases/model/field';
import { type ValueJSON, type SingleTextValue } from '@databases/model/value';
import { type Op } from 'quill-delta';
import * as Y from 'yjs';

import { plainTextToAdf } from '@canvas/adf/conversions';
import { getAdfTextContentWithUrl } from '@canvas/adf/get-adf-text-content';
import { type ADFDocument } from '@canvas/adf-types';

import { type DisposableElement, type WrappedYType } from '../helpers/types.ts';
import { YFieldType } from '../y-field.ts';

import { writeAdfToXmlFragment, xmlFragmentToAdf } from './adf-xml-conversion.ts';

export const TextValueStorageKeys = {
  type: 't',
  plainText: 'v',
  richText: 'rt'
};

type TextLikeFieldType = FieldType.Text | FieldType.AiSummary;

export class YTextValue<T extends TextLikeFieldType = FieldType.Text>
  implements WrappedYType, DisposableElement
{
  #text: Y.Text | undefined;
  #fragment: Y.XmlFragment | undefined;

  constructor(
    private input: Y.Map<any>,
    private attach?: (map: Y.Map<any>) => void,
    private fieldType: T = FieldType.Text as T
  ) {
    input.observe(this.onValueChanged);

    const ref = input.get(TextValueStorageKeys.plainText);
    if (ref instanceof Y.Text) {
      this.#text = ref;
    }

    const fragRef = input.get(TextValueStorageKeys.richText);
    if (fragRef instanceof Y.XmlFragment) {
      this.#fragment = fragRef;
    } else if (input.doc) {
      this.#fragment = new Y.XmlFragment();
      input.set(TextValueStorageKeys.richText, this.#fragment);
    }
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(TextValueStorageKeys.plainText)) {
      const val = this.input.get(TextValueStorageKeys.plainText);
      if (val instanceof Y.Text) {
        this.#text = val;
      } else if (this.#text) {
        this.#text = undefined;
      }
    }
    if (e.keysChanged.has(TextValueStorageKeys.richText)) {
      const val = this.input.get(TextValueStorageKeys.richText);
      if (val instanceof Y.XmlFragment) {
        this.#fragment = val;
      } else if (this.#fragment) {
        this.#fragment = undefined;
      }
    }
  };

  get value(): SingleTextValue {
    const rich = this.richTextValue;
    return {
      plain: this.plainTextValue,
      ...(rich !== undefined && { rich })
    };
  }

  get plainTextValue(): string {
    if (!this.#text) {
      const ref = this.input.get(TextValueStorageKeys.plainText);
      if (ref instanceof Y.Text) {
        this.#text = ref;
      } else {
        return '';
      }
    }
    return this.#text.toJSON();
  }

  get richTextValue(): ADFDocument | undefined {
    if (!this.#fragment) {
      const ref = this.input.get(TextValueStorageKeys.richText);
      if (ref instanceof Y.XmlFragment) {
        this.#fragment = ref;
      } else {
        return undefined;
      }
    }
    if (this.#fragment.length === 0) {
      return undefined;
    }
    return xmlFragmentToAdf(this.#fragment);
  }

  get isEmpty() {
    return !this.#text?.length || this.#text.toJSON().trim() === '';
  }

  private ensureText(): Y.Text {
    if (!this.#text) {
      this.#text = new Y.Text();
      this.input.set(TextValueStorageKeys.plainText, this.#text);
      this.attach?.(this.input);
    }
    return this.#text;
  }

  private ensureFragment(): Y.XmlFragment {
    if (!this.#fragment) {
      this.#fragment = new Y.XmlFragment();
      this.input.set(TextValueStorageKeys.richText, this.#fragment);
    }
    return this.#fragment;
  }

  private syncToFragment(): void {
    const text = this.#text?.toJSON() ?? '';
    const adf = plainTextToAdf(text);
    writeAdfToXmlFragment(adf, this.ensureFragment());
  }

  setContent(adf: ADFDocument): void {
    writeAdfToXmlFragment(adf, this.ensureFragment());

    const text = getAdfTextContentWithUrl(adf, '');
    const yText = this.ensureText();
    yText.delete(0, yText.length);
    if (text) {
      yText.insert(0, text);
    }
  }

  setRichContent(adf: ADFDocument): void {
    writeAdfToXmlFragment(adf, this.ensureFragment());
  }

  insert(index: number, content: string): void {
    this.ensureText().insert(index, content);
    this.syncToFragment();
  }

  delete(index: number, length: number): void {
    this.ensureText().delete(index, length);
    this.syncToFragment();
  }

  applyDelta(delta: Op[]): void {
    this.ensureText().applyDelta(delta);
    this.syncToFragment();
  }

  get type(): T {
    return this.fieldType;
  }

  toJSON(): ValueJSON<T, SingleTextValue> {
    return {
      type: this.type,
      value: this.value
    };
  }

  static fromJSON<T extends TextLikeFieldType>(json: ValueJSON<T, SingleTextValue>) {
    const map = new Y.Map();
    map.set(
      TextValueStorageKeys.type,
      json.type === FieldType.AiSummary ? YFieldType.AI_SUMMARY : YFieldType.TEXT
    );
    map.set(TextValueStorageKeys.plainText, new Y.Text(json.value.plain ?? ''));
    if (json.value.rich) {
      const fragment = new Y.XmlFragment();
      writeAdfToXmlFragment(json.value.rich, fragment);
      map.set(TextValueStorageKeys.richText, fragment);
    }
    return new YTextValue(map, undefined, json.type);
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.input.unobserve(this.onValueChanged);
  }
}
